import {
  Box,
  Stack,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  InputAdornment,
  IconButton,
} from "@mui/material";
import { FilterList, Close as CloseIcon } from "@mui/icons-material";

/**
 * Filters panel component for filtering the bulk tag mapping grid.
 *
 * @param {Object} props - Component props
 * @param {string} props.tagNameFilter - Current tag name filter value
 * @param {Function} props.onTagNameFilterChange - Callback when tag name filter changes
 * @param {string} props.assignedUDFilter - Current assigned UD filter value
 * @param {Function} props.onAssignedUDFilterChange - Callback when assigned UD filter changes
 * @param {string} props.tenantFilter - Current tenant filter value ('All' or tenant ID)
 * @param {Function} props.onTenantFilterChange - Callback when tenant filter changes
 * @param {Array} props.rows - Array of all rows (for extracting distinct tenants)
 * @param {string} props.statusFilter - Current status filter value
 * @param {Function} props.onStatusFilterChange - Callback when status filter changes
 */
const BulkTagMappingFilters = ({
  tagNameFilter,
  onTagNameFilterChange,
  assignedUDFilter,
  onAssignedUDFilterChange,
  tenantFilter,
  onTenantFilterChange,
  rows,
  statusFilter,
  onStatusFilterChange,
}) => {
  // Extract distinct tenants from rows
  const distinctTenants = Array.from(
    new Map(rows.map((row) => [row.tenantId, row.tenantDisplayName])).entries()
  )
    .sort((a, b) => a[1].localeCompare(b[1]))
    .map(([tenantId, displayName]) => ({ id: tenantId, name: displayName }));

  return (
    <Box sx={{ mb: 2 }}>
      <Stack direction='row' alignItems='center' spacing={1} sx={{ mb: 2 }}>
        <FilterList />
        <Typography variant='h6'>Filters</Typography>
      </Stack>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={3}>
          <TextField
            fullWidth
            size='small'
            label='Tag Name'
            variant='outlined'
            value={tagNameFilter}
            onChange={(e) => onTagNameFilterChange(e.target.value)}
            placeholder='Filter by tag name...'
            InputProps={{
              endAdornment: tagNameFilter && (
                <InputAdornment position='end'>
                  <IconButton
                    size='small'
                    onClick={() => onTagNameFilterChange("")}
                    edge='end'>
                    <CloseIcon fontSize='small' />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Grid>
        <Grid item xs={12} sm={3}>
          <TextField
            fullWidth
            size='small'
            label='Assigned UD'
            variant='outlined'
            value={assignedUDFilter}
            onChange={(e) => onAssignedUDFilterChange(e.target.value)}
            placeholder='Filter by Universal Descriptor...'
            InputProps={{
              endAdornment: assignedUDFilter && (
                <InputAdornment position='end'>
                  <IconButton
                    size='small'
                    onClick={() => onAssignedUDFilterChange("")}
                    edge='end'>
                    <CloseIcon fontSize='small' />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Grid>
        <Grid item xs={12} sm={3}>
          <FormControl fullWidth size='small'>
            <InputLabel>Tenant</InputLabel>
            <Select
              value={tenantFilter}
              label='Tenant'
              onChange={(e) => onTenantFilterChange(e.target.value)}>
              <MenuItem value='All'>All Tenants</MenuItem>
              {distinctTenants.map((tenant) => (
                <MenuItem key={tenant.id} value={tenant.id.toString()}>
                  {tenant.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={3}>
          <FormControl fullWidth size='small'>
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              label='Status'
              onChange={(e) => onStatusFilterChange(e.target.value)}>
              <MenuItem value='All'>All Tags</MenuItem>
              <MenuItem value='Unassigned'>Unassigned</MenuItem>
              <MenuItem value='Assigned'>Assigned</MenuItem>
            </Select>
          </FormControl>
        </Grid>
      </Grid>
    </Box>
  );
};

export default BulkTagMappingFilters;
